export type CatalogKind = 'tools' | 'narratives';

const SITE_URL = 'https://scientata.com';

interface CatalogItem {
  title: string;
  displayTitle: string;
  url: string;
  category: string;
  categoryLabel: string;
  meta: string;
  description: string;
  details: string;
  features: string[];
  keywords?: string[];
  image?: string;
  art?: string;
  accent: string;
}

const tools: CatalogItem[] = [
  {
    title: 'Scientata Canvas', displayTitle: 'Scientata <em>Canvas</em>', url: 'https://canvas.scientata.com',
    category: 'metodologia', categoryLabel: 'Metodologia & DSR', meta: 'Projeto de pesquisa · Coerência · Local-first',
    description: 'Estruture, alinhe e comunique sua pesquisa: problema, propósito, abordagem e planejamento em uma mesma superfície de reflexão.',
    details: 'Conduz o projeto por etapas guiadas — contexto, propósito, abordagem e planejamento — com perguntas de reflexão sobre o alinhamento entre problema, objetivos e método. Modo apresentação e exportações facilitam o diálogo com orientação e banca.',
    features: ['Etapas guiadas', 'Coerência entre problema e método', 'Sem conta, dados locais'],
    image: '/apps/canvas.jpg', accent: '#b8ff4a',
  },
  {
    title: 'Simetrics', displayTitle: 'Si<em>metrics</em>', url: 'https://simetrics.app',
    category: 'analise', categoryLabel: 'Análise científica', meta: 'Bibliometria · Cientometria · Redes',
    description: 'Transforme exports da Scopus, Web of Science, SciELO e outras bases em indicadores cientométricos, redes de colaboração e mapeamento temático por IA.',
    details: 'Mapeia a estrutura intelectual da sua base com grafos, métricas de rede, PCA e similaridade, além de categorização temática automática e um assistente conversacional sobre os artigos. Todo o processamento roda em Web Workers, no navegador.',
    features: ['Ecologia do conhecimento', 'Categorização temática por IA', '100% no navegador'],
    image: '/apps/simetrics.jpg', accent: '#6a7dff',
  },
  {
    title: 'Scientata DSR', displayTitle: 'Scientata <em>DSR</em>', url: 'https://dsr.scientata.com',
    category: 'metodologia', categoryLabel: 'Metodologia & DSR', meta: 'Design Science Research · Peffers · Local-first',
    description: 'Construa uma pesquisa em Design Science coerente, visual e rastreável, com os projetos guardados no seu dispositivo.',
    details: 'Organiza problema, objetivos, construção, demonstração, avaliação e comunicação em um processo guiado, com exemplo didático, tour e backups exportáveis. Sem conta e sem envio do conteúdo da pesquisa.',
    features: ['Processo guiado em 6 etapas', 'Exemplo didático e tour', 'Sem conta, dados locais'],
    image: '/apps/dsr.jpg', accent: '#1ea896',
  },
  {
    title: 'PRISMA Lab', displayTitle: 'PRISMA <em>Lab</em>', url: 'https://prisma.scientata.com',
    category: 'revisao', categoryLabel: 'Revisão sistemática', meta: 'PRISMA 2020 · Validação · Diagramas',
    description: 'Construa, verifique e publique diagramas PRISMA 2020 com validação explicável, checklist dos 27 itens e cálculos rastreáveis.',
    details: 'Preserva a distinção entre registros, relatos e estudos sem corrigir dados silenciosamente: cada alerta mostra o que ocorreu, por que importa e quais valores revisar. Exporta PDF e SVG vetoriais, PNG, HTML interativo, relatório e pacote ZIP.',
    features: ['4 modelos oficiais', '6 idiomas', 'PDF, SVG, HTML e ZIP'],
    image: '/apps/prisma.jpg', accent: '#f5bd59',
  },
  {
    title: 'KADima', displayTitle: 'KAD<em>ima</em>', url: 'https://kadima.scientata.com',
    category: 'conhecimento', categoryLabel: 'Engenharia do conhecimento', meta: 'CommonKADS · Modelagem · Local-first',
    description: 'Aplique o CommonKADS do começo ao fim: preencha as dezesseis planilhas do método e gere diagramas e o dossiê final automaticamente.',
    details: 'Guia as planilhas da OM-1 à DM-4, aponta referências quebradas e lacunas entre os modelos e exporta o dossiê em PDF e DOCX, com diagramas em SVG e PNG. Funciona offline, em português e inglês, com código aberto sob Apache 2.0.',
    features: ['16 planilhas guiadas', '7 diagramas gerados', 'Offline e código aberto'],
    image: '/apps/kadima.jpg', accent: '#5865f2',
  },
  {
    title: 'Ontouro', displayTitle: 'On<em>touro</em>', url: 'https://ontouro.scientata.com',
    category: 'conhecimento', categoryLabel: 'Engenharia do conhecimento', meta: 'Ontologias OWL · Diagramas · IA assistiva',
    description: 'Modele o conhecimento com método: ontologias OWL e diagramas UML, ER e mais, com um assistente de IA que propõe — e você decide.',
    details: 'Constrói ontologias OWL 2 guiadas com raciocinador, diagramas UML, ER e fluxos, ou ambos conectados. Importa modelos existentes e mantém tudo salvo no navegador.',
    features: ['OWL 2 com raciocinador', 'UML, ER e fluxos', 'Salvo no navegador'],
    image: '/apps/ontouro.jpg', accent: '#e8667f',
  },
  {
    title: 'OnePDF', displayTitle: 'OnePDF', url: 'https://onepdf.scientata.com',
    category: 'documentos', categoryLabel: 'Documentos', meta: 'PDF · Organização · Local-first',
    description: 'Junte, divida, converta, edite, assine e aplique OCR em PDFs inteiramente no navegador.',
    details: 'Reúne 26 ferramentas para organizar, converter de e para Word, Excel e PowerPoint, editar, anotar e proteger documentos. Os arquivos não saem do dispositivo e nenhuma conta é necessária.',
    features: ['26 ferramentas', 'Word, Excel e PowerPoint', 'Zero uploads'],
    image: '/apps/onepdf.jpg', accent: '#6d9cff',
  },
  {
    title: 'MDForge', displayTitle: 'MDForge', url: 'https://mdforge.scientata.com',
    category: 'documentos', categoryLabel: 'Documentos', meta: 'Conversão · Markdown · 11 formatos',
    description: 'Converta arquivos e pastas inteiras em Markdown, revise o resultado e exporte com privacidade.',
    details: 'Prepara documentos para bases de conhecimento, documentação e fluxos com IA. A prévia editável ajuda a corrigir estrutura e conteúdo antes da saída final.',
    features: ['PDF, DOCX e mais', 'Filas e pastas', 'Prévia editável'],
    image: '/apps/mdforge.jpg', accent: '#e56d45',
  },
  {
    title: 'TokenLab', displayTitle: 'Token<em>Lab</em>', url: 'https://tokenlab.scientata.com',
    category: 'ia', categoryLabel: 'IA aplicada', meta: 'RAG · Tokens · Embeddings',
    description: 'Conte tokens, simule estratégias de chunking e estime a carga dos documentos antes da indexação.',
    details: 'Torna visível a preparação de conteúdo para sistemas de recuperação e geração, permitindo comparar modelos, tamanhos de trecho e sobreposição.',
    features: ['Múltiplos modelos', 'Simulação de chunks', 'Análise exportável'],
    image: '/apps/tokenlab.jpg', accent: '#b8ff4a',
  },
  {
    title: 'DataVizLab', displayTitle: 'DataViz<em>Lab</em>', url: 'https://datavizlab.scientata.com',
    category: 'visualizacao', categoryLabel: 'Visualização de dados', meta: 'Atlas · Recomendador · Estúdio local',
    description: 'Explore métodos, encontre a visualização adequada à sua pergunta e construa gráficos com seus próprios dados.',
    details: 'Combina um atlas bilíngue de métodos, recomendações orientadas pela intenção analítica e um estúdio que processa CSV e Excel localmente.',
    features: ['78 métodos documentados', '10 famílias visuais', 'Arquivos processados localmente'],
    image: '/apps/datavizlab.jpg', accent: '#ff6846',
  },
];

export const narratives: CatalogItem[] = [
  {
    title: 'Teoria da Informação', displayTitle: 'Teoria da <em>Informação</em>', url: 'https://shannon.scientata.com',
    category: 'ciencia', categoryLabel: 'Ciência & tecnologia', meta: 'Artigo seminal · Claude Shannon · 1948',
    description: 'Uma jornada em sete atos pela publicação que quantificou a informação e fundou matematicamente a era digital.',
    details: 'Conceitos como bit, entropia, redundância e capacidade de canal ganham explicações visuais e experimentos que aproximam a teoria da experiência.',
    features: ['Bit e entropia', 'Canais com ruído', 'Experimentos interativos'],
    keywords: ['Informação e sinais', 'Matemática', 'Comunicação e mídia'],
    image: '/narratives/shannon.jpg', accent: '#e9a13b',
  },
  {
    title: 'Attention Is All You Need', displayTitle: 'Atenção é tudo<br>o que você <em>precisa.</em>', url: 'https://attention.scientata.com',
    category: 'ciencia', categoryLabel: 'Ciência & tecnologia', meta: 'Artigo seminal · Vaswani et al. · NIPS 2017',
    description: 'Uma jornada scrollytelling pelo artigo que introduziu os Transformers e redefiniu a inteligência artificial.',
    details: 'Self-attention, a arquitetura encoder-decoder e os resultados de tradução automática ganham explicações visuais fiéis aos números e tabelas do artigo original.',
    features: ['Self-attention explicada', 'Tabelas do artigo original', 'Experiência bilíngue PT/EN'],
    keywords: ['Inteligência artificial', 'IA generativa', 'Linguagem'],
    image: '/narratives/attention.jpg', accent: '#38bdf8',
  },
  {
    title: 'Ouvir o som. Localizar o espaço.', displayTitle: 'Ouvir o som.<br><em>Localizar o espaço.</em>', url: 'https://tcc.gustavosimas.com',
    category: 'ciencia', categoryLabel: 'Ciência & tecnologia', meta: 'TCC · Engenharia Eletrônica · UFSC · 2021',
    description: 'O desafio de reduzir ruído em aparelhos auditivos sem apagar as pistas que permitem localizar a origem do som.',
    details: 'A narrativa traduz processamento biauricular, filtragem e percepção espacial em uma experiência acompanhada por demonstrações sonoras.',
    features: ['Processamento biauricular', 'Filtro de Wiener', 'Demonstração sonora'],
    keywords: ['Informação e sinais', 'Cognição e percepção'],
    image: '/narratives/tcc.jpg', accent: '#a85438',
  },
  {
    title: 'Conhecimento é ecossistema', displayTitle: 'Conhecimento é <em>ecossistema.</em>', url: 'https://dissertacao.gustavosimas.com',
    category: 'conhecimento', categoryLabel: 'Conhecimento & gestão', meta: 'Dissertação · PPGEGC · UFSC · 2024',
    description: 'O metamodelo ECO-CAOs traduzido em uma experiência sobre como o conhecimento nasce, circula e se transforma.',
    details: 'Atores, recursos, atividades, instituições e relações formam um percurso interativo pelas estruturas que sustentam ecossistemas de conhecimento.',
    features: ['8 capítulos interativos', 'Metamodelo ECO-CAOs', 'Evidências rastreáveis'],
    keywords: ['Gestão do conhecimento', 'Engenharia do conhecimento', 'Modelagem'],
    image: '/narratives/dissertacao.jpg', accent: '#8fcf3e',
  },
  {
    title: 'Humano–IA: antropomorfização', displayTitle: 'Humano–IA:<br><em>antropomorfização</em>', url: 'https://ihsi2024.scientata.com',
    category: 'ia_sociedade', categoryLabel: 'IA & sociedade', meta: 'Artigo científico · AHFE / IHSI · 2024',
    description: 'Uma síntese sobre como o ChatGPT ativa nossa cognição social, elevando engajamento e confiança — e também riscos.',
    details: 'A experiência organiza evidências sobre percepção de humanidade, formação de vínculo, confiança excessiva e os limites da interação humano–IA.',
    features: ['Modelo SEEK', 'Benefícios e riscos', 'Matriz de evidências'],
    keywords: ['Inteligência artificial', 'Cognição e percepção', 'Linguagem', 'Sociedade'],
    image: '/narratives/ihsi2024.jpg', accent: '#55badc',
  },
  {
    title: 'Engenharia e Gestão do Conhecimento', displayTitle: 'Engenharia e Gestão<br>do <em>Conhecimento</em>', url: 'https://commonkads.scientata.com',
    category: 'conhecimento', categoryLabel: 'Conhecimento & gestão', meta: 'CommonKADS · Schreiber et al. · 2000',
    description: 'Uma narrativa visual interativa sobre a metodologia CommonKADS e a construção de sistemas intensivos em conhecimento.',
    details: 'Apresenta o conjunto de modelos que ajuda a compreender organizações, tarefas, agentes, conhecimento, comunicação e desenho de soluções.',
    features: ['Metodologia CommonKADS', 'Modelagem organizacional', 'Experiência bilíngue PT/EN'],
    keywords: ['Engenharia do conhecimento', 'Gestão do conhecimento', 'Modelagem'],
    image: '/narratives/commonkads.jpg', accent: '#70d6b2',
  },
  {
    title: 'Trajetórias de Mulheres Negras no Ensino Superior', displayTitle: 'Trajetórias de<br><em>Mulheres Negras</em>', url: 'https://trajetoriamulheresnegras.scientata.com',
    category: 'sociedade', categoryLabel: 'Sociedade & educação', meta: 'Artigo científico · Ensino superior brasileiro',
    description: 'Dados, barreiras, conquistas e caminhos que atravessam a presença de mulheres negras no ensino superior.',
    details: 'O percurso conecta ingresso, permanência, pós-graduação, carreira docente e transformação institucional, sempre ancorado nas evidências do artigo.',
    features: ['8 atos narrativos', 'Indicadores contextualizados', 'Barreiras e redes de resistência'],
    keywords: ['Sociedade', 'Ensino superior'],
    image: '/narratives/trajetorias.jpg', accent: '#d9809a',
  },
  {
    title: 'Promptografia', displayTitle: '<em>Promptografia</em>', url: 'https://promptografia.scientata.com',
    category: 'ia_sociedade', categoryLabel: 'IA & sociedade', meta: 'Artigo científico · Estudos CTS · 2026',
    description: 'Uma investigação sobre a reconfiguração da agência criativa humana na produção de imagens com IA generativa.',
    details: 'Índice e símbolo, aparato técnico, aceleração da imagem e simulacro organizam uma reflexão sobre por que imagens geradas por IA não são fotografias.',
    features: ['Agência criativa', 'Flusser e o aparato', 'Imagem generativa e simulacro'],
    keywords: ['IA generativa', 'Filosofia da técnica', 'Sociedade'],
    image: '/narratives/promptografia.jpg', accent: '#ff765d',
  },
  {
    title: 'KEMM na Sociedade do Conhecimento', displayTitle: 'KEMM na Sociedade<br>do <em>Conhecimento</em>', url: 'https://eckm2024.scientata.com',
    category: 'conhecimento', categoryLabel: 'Conhecimento & gestão', meta: 'Artigo científico · ECKM · 2024',
    description: 'Uma agenda de pesquisa sobre o papel da engenharia, gestão e mídia do conhecimento na sociedade contemporânea.',
    details: 'A narrativa explora como as interseções KEMM impulsionam inovação, modelos culturais e novas perguntas em organizações intensivas em conhecimento.',
    features: ['Agenda de pesquisa', 'Interseções KEMM', 'Narrativa bilíngue PT/EN'],
    keywords: ['Gestão do conhecimento', 'Engenharia do conhecimento', 'Comunicação e mídia'],
    image: '/narratives/eckm2024.jpg', accent: '#818cf8',
  },
  {
    title: 'Tecnonecromancia', displayTitle: 'Tecno<em>necromancia</em>', url: 'https://tecnonecromancia.scientata.com',
    category: 'ia_sociedade', categoryLabel: 'IA & sociedade', meta: 'Artigo científico · Estudos CTS · 2026',
    description: 'Um ensaio crítico sobre deepfakes, clones de voz e chatbots de luto — e como essas tecnologias miméticas reconfiguram a fronteira entre vida, morte e presença.',
    details: 'Baudrillard e Derrida sustentam a proposta do neologismo tecnonecromancia, confrontado com estudos de caso reais e uma tabela que separa o que um modelo póstumo retém do que depende da cognição corporificada.',
    features: ['Neologismo tecnonecromancia', 'Baudrillard, Derrida e as 4E', 'Estudos de caso reais'],
    keywords: ['IA generativa', 'Filosofia da técnica', 'Sociedade'],
    image: '/narratives/tecnonecromancia.jpg', accent: '#8a7ca8',
  },
  {
    title: 'On the Measure of Intelligence', displayTitle: 'Como se mede<br>a <em>inteligência?</em>', url: 'https://measureofintelligence.scientata.com',
    category: 'ciencia', categoryLabel: 'Ciência & tecnologia', meta: 'Artigo seminal · François Chollet · 2019',
    description: 'Uma leitura guiada pelo artigo que separa habilidade de inteligência e propõe uma definição formal e um teste novo para medi-la.',
    details: 'Da escada da generalização à inteligência como eficiência, a narrativa chega ao ARC — quebra-cabeças em grades que não dá para decorar —, com três níveis de profundidade e cada afirmação ancorada na página de origem.',
    features: ['Três níveis de leitura', 'Quebra-cabeças ARC interativos', 'Experiência bilíngue PT/EN'],
    keywords: ['Inteligência artificial', 'Cognição e percepção', 'Matemática'],
    image: '/narratives/measureofintelligence.jpg', accent: '#ff8257',
  },
];

const pageConfig = {
  tools: {
    eyebrow: 'Aplicações · ferramentas para pesquisa',
    title: 'Instrumentos para avançar do <em>arquivo à descoberta.</em>',
    summary: 'Uma coleção de aplicações focadas em tarefas concretas da pesquisa — com privacidade, clareza metodológica e autonomia sempre que possível.',
    label: 'ferramentas',
    search: 'Buscar ferramenta, tarefa ou formato…',
  },
  narratives: {
    eyebrow: 'Narrativas digitais · conhecimento em movimento',
    title: 'Pesquisa feita para <em>explorar, ouvir e compreender.</em>',
    summary: 'Experiências digitais que traduzem artigos, dissertações e trabalhos acadêmicos sem abandonar contexto, evidências ou rigor.',
    label: 'narrativas',
    search: 'Buscar narrativa, tema ou conceito…',
  },
};

const setMetadata = (kind: CatalogKind) => {
  const isTools = kind === 'tools';
  const title = isTools ? 'Ferramentas para pesquisa — Scientata' : 'Narrativas digitais — Scientata';
  const description = 'Um ecossistema digital com ferramentas para pesquisa e narrativas digitais de publicações. Sua pesquisa acadêmica com visão e inteligência.';
  document.title = title;
  document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute('content', description);
  document.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.setAttribute('content', title);
  document.querySelector<HTMLMetaElement>('meta[property="og:description"]')?.setAttribute('content', description);
  document.querySelector<HTMLMetaElement>('meta[property="og:url"]')?.setAttribute('content', `https://scientata.com/${kind}`);
  document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]')?.setAttribute('content', title);
  document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]')?.setAttribute('content', description);
  document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute('href', `https://scientata.com/${kind}`);
};

const renderCard = (item: CatalogItem, index: number, total: number) => `
  <article class="catalog-card" data-category="${item.category}" data-search="${[item.title, item.categoryLabel, item.meta, item.description, item.details, ...item.features].join(' ').toLocaleLowerCase('pt-BR')}" style="--card-accent:${item.accent}">
    <a class="catalog-card-visual" href="${item.url}" target="_blank" rel="noopener" aria-label="Abrir ${item.title}">
      ${item.image
        ? `<img src="${item.image}" alt="Prévia de ${item.title}" width="1280" height="720" loading="lazy">`
        : `<span class="catalog-card-art" aria-hidden="true"><i>${item.art}</i><b>${item.categoryLabel}</b></span>`}
      <span class="catalog-card-index">${String(index + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}</span>
    </a>
    <div class="catalog-card-body">
      <div class="catalog-card-meta"><span>${item.categoryLabel}</span><small>${item.meta}</small></div>
      <h3>${item.displayTitle}</h3>
      <p class="catalog-card-lead">${item.description}</p>
      <p class="catalog-card-detail">${item.details}</p>
      <ul aria-label="Destaques de ${item.title}">${item.features.map((feature) => `<li>${feature}</li>`).join('')}</ul>
      <a class="catalog-card-link" href="${item.url}" target="_blank" rel="noopener">${item.url.replace(/^https?:\/\//, '')} <span>↗</span></a>
    </div>
  </article>`;

export type ShellRoute = 'tools' | 'narratives' | 'content';

export const renderShellHeader = (current?: ShellRoute) => {
  const mark = (route: ShellRoute) => (route === current ? ' aria-current="page"' : '');
  return `<header class="catalog-header">
      <a class="brand" href="/" aria-label="Scientata — início"><span class="brand-mark" aria-hidden="true">S<span>+</span></span><span>Scientata</span></a>
      <nav id="primary-nav" aria-label="Navegação principal"><a href="/tools"${mark('tools')}>Ferramentas</a><a href="/narratives"${mark('narratives')}>Narrativas</a><a href="/content"${mark('content')}>Conteúdo</a></nav>
      <button type="button" class="nav-toggle" aria-expanded="false" aria-controls="primary-nav" aria-label="Abrir menu de navegação"><span class="nav-toggle-bar"></span><span class="nav-toggle-bar"></span><span class="nav-toggle-bar"></span></button>
      <a class="catalog-home-link" href="/">Voltar ao início <span>↖</span></a>
    </header>`;
};

export const renderShellFooter = () => `<footer class="catalog-footer">
      <a class="brand footer-brand" href="/"><span class="brand-mark" aria-hidden="true">S<span>+</span></span><span>Scientata</span></a>
      <p>Pesquisa com visão e inteligência</p>
      <div class="footer-links"><a href="/tools">Ferramentas</a><a href="/narratives">Narrativas</a><a href="/content">Conteúdo</a><a href="mailto:email@scientata.com">Contato</a></div>
      <small>© <span id="year"></span> Scientata · Florianópolis, Brasil</small>
    </footer>`;

const renderPage = (kind: CatalogKind, items: CatalogItem[]) => {
  const config = pageConfig[kind];
  const categories = Array.from(new Map(items.map((item) => [item.category, item.categoryLabel])).entries());
  return `
    <a class="skip-link" href="#catalogo">Pular para o catálogo</a>
    ${renderShellHeader(kind)}
    <main class="catalog-main" id="conteudo">
      <section class="catalog-hero">
        <div class="catalog-grid-field" aria-hidden="true"></div>
        <p class="catalog-route">SCNT / ${kind === 'tools' ? 'TOOLS' : 'NARRATIVES'} / ${String(items.length).padStart(2, '0')}</p>
        <div class="catalog-hero-copy">
          <p class="eyebrow"><span></span>${config.eyebrow}</p>
          <h1>${config.title}</h1>
          <p>${config.summary}</p>
        </div>
        <a class="catalog-scroll" href="#catalogo">Explorar ${config.label} <span>↓</span></a>
      </section>

      <section class="catalog-content" id="catalogo" aria-labelledby="catalog-title">
        <div class="catalog-intro">
          <div><p class="section-kicker">Catálogo completo</p><h2 id="catalog-title">Encontre o que a sua <em>pesquisa pede.</em></h2></div>
          <p>Busque livremente ou selecione uma categoria. Cada item abre a experiência original em uma nova aba.</p>
        </div>
        <div class="catalog-controls" aria-label="Busca e filtros do catálogo">
          <label class="catalog-search"><span aria-hidden="true">⌕</span><span class="sr-only">Buscar no catálogo</span><input type="search" data-catalog-search placeholder="${config.search}" autocomplete="off"><kbd>⌘ K</kbd></label>
          <div class="catalog-filters" role="group" aria-label="Filtrar por categoria">
            <button type="button" class="is-active" data-category-filter="all" aria-pressed="true">Todas <span>${items.length}</span></button>
            ${categories.map(([value, label]) => `<button type="button" data-category-filter="${value}" aria-pressed="false">${label} <span>${items.filter((item) => item.category === value).length}</span></button>`).join('')}
          </div>
          <p class="catalog-results" aria-live="polite"><strong data-results-count>${items.length}</strong> ${config.label} encontradas</p>
        </div>
        <div class="catalog-list">${items.map((item, index) => renderCard(item, index, items.length)).join('')}</div>
        <div class="catalog-pagination" data-catalog-pagination hidden>
          <button type="button" data-page-prev><span>←</span> Anterior</button>
          <span data-page-indicator></span>
          <button type="button" data-page-next>Próximo <span>→</span></button>
        </div>
        <div class="catalog-empty" hidden data-catalog-empty><span>∅</span><h2>Nenhum resultado.</h2><p>Tente outro termo ou remova o filtro selecionado.</p><button type="button" data-clear-filters>Limpar busca e filtros</button></div>
      </section>
    </main>
    ${renderShellFooter()}`;
};

const renderCatalogJsonLd = (kind: CatalogKind, items: CatalogItem[]) => {
  const pageUrl = `${SITE_URL}/${kind}`;
  const pageLabel = kind === 'tools' ? 'Ferramentas' : 'Narrativas';
  const itemType = kind === 'tools' ? 'SoftwareApplication' : 'CreativeWork';

  const itemListElement = items.map((item, index) => {
    const entity: Record<string, unknown> = {
      '@type': itemType,
      name: item.title,
      description: item.description,
      url: item.url,
    };
    if (kind === 'tools') {
      entity.applicationCategory = item.categoryLabel;
      entity.operatingSystem = 'Web';
    } else {
      entity.author = { '@type': 'Person', name: 'Gustavo Simas da Silva' };
      entity.publisher = { '@type': 'Organization', name: 'Scientata', url: SITE_URL };
    }
    return { '@type': 'ListItem', position: index + 1, item: entity };
  });

  return JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'ItemList', name: pageLabel, url: pageUrl, itemListElement },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Scientata', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: pageLabel, item: pageUrl },
        ],
      },
    ],
  });
};

export const renderCatalogSSR = (kind: CatalogKind) => {
  const items = kind === 'tools' ? tools : narratives;
  return { bodyHtml: renderPage(kind, items), jsonLd: renderCatalogJsonLd(kind, items) };
};

const ROWS_PER_PAGE = 2;

const setupCatalog = (kind: CatalogKind) => {
  const input = document.querySelector<HTMLInputElement>('[data-catalog-search]');
  const list = document.querySelector<HTMLElement>('.catalog-list');
  const cards = Array.from(document.querySelectorAll<HTMLElement>('.catalog-card'));
  const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-category-filter]'));
  const count = document.querySelector<HTMLElement>('[data-results-count]');
  const empty = document.querySelector<HTMLElement>('[data-catalog-empty]');
  const pagination = document.querySelector<HTMLElement>('[data-catalog-pagination]');
  const prevButton = document.querySelector<HTMLButtonElement>('[data-page-prev]');
  const nextButton = document.querySelector<HTMLButtonElement>('[data-page-next]');
  const pageIndicator = document.querySelector<HTMLElement>('[data-page-indicator]');
  const activeCategories = new Set<string>();
  let page = 0;

  const columnsPerRow = () => {
    if (!list) return 1;
    return getComputedStyle(list).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
  };

  const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR').trim();
  const syncButtons = () => {
    buttons.forEach((button) => {
      const value = button.dataset.categoryFilter ?? 'all';
      const active = value === 'all' ? activeCategories.size === 0 : activeCategories.has(value);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  };
  const update = () => {
    const query = normalize(input?.value ?? '');
    const matches = cards.filter((card) => {
      const searchable = normalize(card.dataset.search ?? '');
      const categoryMatches = activeCategories.size === 0 || activeCategories.has(card.dataset.category ?? '');
      const searchMatches = !query || searchable.includes(query);
      return categoryMatches && searchMatches;
    });

    const perPage = columnsPerRow() * ROWS_PER_PAGE;
    const pageCount = Math.max(1, Math.ceil(matches.length / perPage));
    page = Math.min(page, pageCount - 1);
    const start = page * perPage;
    const visibleOnPage = new Set(matches.slice(start, start + perPage));
    cards.forEach((card) => { card.hidden = !visibleOnPage.has(card); });

    if (count) count.textContent = String(matches.length);
    if (empty) empty.hidden = matches.length !== 0;
    if (pagination) pagination.hidden = pageCount <= 1;
    if (prevButton) prevButton.disabled = page === 0;
    if (nextButton) nextButton.disabled = page >= pageCount - 1;
    if (pageIndicator) pageIndicator.textContent = `${page + 1} / ${pageCount}`;
  };

  const initialQuery = new URLSearchParams(window.location.search).get('q');
  if (initialQuery && input) input.value = initialQuery;
  update();

  input?.addEventListener('input', () => { page = 0; update(); });
  buttons.forEach((button) => button.addEventListener('click', () => {
    const value = button.dataset.categoryFilter ?? 'all';
    if (value === 'all') {
      activeCategories.clear();
    } else if (activeCategories.has(value)) {
      activeCategories.delete(value);
    } else {
      activeCategories.add(value);
    }
    syncButtons();
    page = 0;
    update();
  }));
  document.querySelector<HTMLButtonElement>('[data-clear-filters]')?.addEventListener('click', () => {
    if (input) input.value = '';
    buttons.find((button) => button.dataset.categoryFilter === 'all')?.click();
    input?.focus();
  });
  prevButton?.addEventListener('click', () => { page = Math.max(0, page - 1); update(); });
  nextButton?.addEventListener('click', () => { page += 1; update(); });
  let resizeTimer: number | undefined;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(update, 150);
  });
  document.addEventListener('keydown', (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      input?.focus();
    }
    if (event.key === 'Escape' && document.activeElement === input) {
      if (input) input.value = '';
      page = 0;
      update();
      input?.blur();
    }
  });

  document.body.className = `catalog-page catalog-${kind}`;
};

export const renderCatalogRoute = () => {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  const kind: CatalogKind | null = path === '/tools' ? 'tools' : path === '/narratives' ? 'narratives' : null;
  if (!kind) return false;
  const items = kind === 'tools' ? tools : narratives;
  setMetadata(kind);
  if (!document.querySelector('.catalog-main')) {
    document.body.innerHTML = renderPage(kind, items);
  }
  setupCatalog(kind);
  return true;
};
